/**
 * Profile fields that become `sameAs` edges on the public profile's JSON-LD.
 *
 * Lives outside the `'use server'` actions file on purpose: a "use server" module may only
 * export async functions, so exporting this constant from there fails the build with
 * `A "use server" file can only export async functions, found object`.
 *
 * `sameAs` is how a search engine or AI engine decides two pages describe the same entity.
 * Without it, a member's Instagram and LinkedIn are invisible to entity resolution — the
 * platform cannot tell that the profile on zamzi.co.za and the Instagram account are the
 * same business. In the 2026-10-03 baseline, linkedin.com was the #2 most-retrieved domain
 * across all three engines (60 citations), so member-supplied links to those properties are
 * the cheapest entity corroboration available.
 */
export interface SocialLinkField {
  label: string;
  field: string;
  placeholder: string;
  help: string;
}

export const SOCIAL_LINK_FIELDS: SocialLinkField[] = [
  {
    label: 'Website',
    field: 'website',
    placeholder: 'https://yourbusiness.co.za',
    help: 'Your own website, if you have one.',
  },
  {
    label: 'Facebook page',
    field: 'facebookPageUrl',
    placeholder: 'https://facebook.com/yourbusiness',
    help: 'The page, not a personal profile.',
  },
  {
    label: 'Instagram',
    field: 'instagramUrl',
    placeholder: 'https://instagram.com/yourbusiness',
    help: 'Must be your public business account.',
  },
  {
    label: 'LinkedIn',
    field: 'linkedinUrl',
    placeholder: 'https://linkedin.com/company/yourbusiness',
    help: 'Company page if you have one.',
  },
  {
    label: 'YouTube',
    field: 'youtubeUrl',
    placeholder: 'https://youtube.com/@yourbusiness',
    help: 'Your channel, even if it has few videos.',
  },
  {
    label: 'Google Business Profile',
    field: 'googleBusinessProfileUrl',
    placeholder: 'https://g.page/yourbusiness',
    help: 'Strong local-entity signal. Shortened GBP links are fine.',
  },
  {
    label: 'Google Maps',
    field: 'googleMapsLink',
    placeholder: 'https://maps.google.com/?cid=123456',
    help: 'Your business location listing.',
  },
  {
    label: 'Founder / social profile',
    field: 'founderSocialProfileUrl',
    placeholder: 'https://linkedin.com/in/yourname',
    help: 'For founder-led businesses, your own professional profile.',
  },
  {
    label: 'Menu link (restaurants)',
    field: 'menuLink',
    placeholder: 'https://yourbusiness.co.za/menu',
    help: 'A direct link to your menu improves restaurant recommendations.',
  },
];

/** Just the field names — used to decide which keys to persist from user input. */
export const SOCIAL_LINK_FIELD_NAMES = SOCIAL_LINK_FIELDS.map((entry) => entry.field);

/** Seed a form state object from a profile document. */
export function seedSocialLinks(
  profile: Record<string, unknown> | null | undefined,
): Record<string, string> {
  const source = profile ?? {};
  const seeded: Record<string, string> = {};
  for (const { field } of SOCIAL_LINK_FIELDS) {
    const value = source[field];
    seeded[field] = typeof value === 'string' ? value : '';
  }
  return seeded;
}